import React, { useEffect, useState, useMemo, useCallback, useRef } from 'react';
import useProtecaoDeRota from '../hooks/useProtecaoDeRota';
import { Spin, message } from 'antd';
import TabelaDados from '../componentes/Table';
import useApi from '../hooks/useApi';
import ModalDelete from '../componentes/modal/DeleteUsuario';
import ModalAdd from '../componentes/modal/AddUsuario';
import ModalEdit from '../componentes/modal/EditarUsuario';

const PaginaUsuarios = () => {
  const verificando = useProtecaoDeRota();
  const [usuarios, setUsuarios] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [modalVisible, setModalVisible] = useState(false);
  const [modalType, setModalType] = useState(null);
  const [selectedId, setSelectedId] = useState(null);
  const previousUsuariosRef = useRef([]); // Ref para armazenar dados anteriores

  const { callApi } = useApi('https://vision.alfama.com/api/view/usuarios', {
    includeToken: true,
  });

  const extraColumns = useMemo(() => [
    { id: 'nome', name: 'Nome', sort: true, mwidth: '5%' },
    { id: 'nome_usuario', name: 'Usuario', sort: true, mwidth: '10%' },
    { id: 'email', name: 'Email', sort: true, width: '15%' },
    { id: 'tipo_usuario', name: 'Tipo', sort: true, width: '10%' },
    { id: 'nome_departamento', name: 'Departamento', sort: true, width: '15%' },
  ], []);

  const showMessage = useCallback((type, content) => {
    message[type](content);
  }, []);

  const formatarUsuarios = useCallback((dados) => {
    return dados.map(usuario => ({
      id: usuario.usuario_id,
      nome: usuario.nome_pessoa,
      nome_usuario: usuario.nome_usuario,
      email: usuario.email,
      status: usuario.situacao_usuario,
      tipo_usuario: usuario.tipo_usuario,
      nome_departamento: usuario.nome_departamento,
      departamento_id: usuario.departamento_id,
      horario_entrada: usuario.horario_entrada,
      horario_saida: usuario.horario_saida,
      permissoes: usuario.grupos_permissao,
    }));
  }, []);

  const buscarUsuarios = useCallback(async () => {
    try {
      const resultado = await callApi();
      const usuariosFormatados = formatarUsuarios(resultado);

      // Verifica se os dados mudaram
      if (JSON.stringify(usuariosFormatados) !== JSON.stringify(previousUsuariosRef.current)) {
        setUsuarios(usuariosFormatados);
        previousUsuariosRef.current = usuariosFormatados; // Atualiza os dados anteriores
      }
    } catch (erro) {
      console.error('Erro ao buscar usuários:', erro);
      showMessage('error', 'Não foi possível carregar os usuários');
    } finally {
      setIsLoading(false);
    }
  }, [callApi, formatarUsuarios, showMessage]);

  const handleModalOpen = useCallback((type, id) => {
    setModalType(type);
    setSelectedId(id);
    setModalVisible(true);
  }, []);

  const handleModalClose = useCallback(() => {
    setModalVisible(false);
    setModalType(null);
    setSelectedId(null);
    buscarUsuarios(); // Atualiza os usuários ao fechar qualquer modal
  }, [buscarUsuarios]);

  const handleDeleteConfirm = useCallback(async (success) => {
    if (success) {
      await buscarUsuarios();
    }
    handleModalClose();
  }, [buscarUsuarios, handleModalClose]);

  const handleEditConfirm = useCallback(async (success) => {
    if (success) {
      await buscarUsuarios();
    }
    handleModalClose();
  }, [buscarUsuarios, handleModalClose]);

  const handleAddConfirm = useCallback(async (success) => {
    if (success) {
      await buscarUsuarios();
    }
    handleModalClose();
  }, [buscarUsuarios, handleModalClose]);

  const getSelectedUsuario = useCallback((id) => {
    return usuarios.find(usuario => usuario.id === id) || {};
  }, [usuarios]);

  useEffect(() => {
    if (!verificando) {
      buscarUsuarios();
    }

    const intervalId = setInterval(() => {
      buscarUsuarios(); // Atualiza os usuários a cada 5 minutos
    }, 300000); // Atualização a cada 5 minutos

    return () => {
      clearInterval(intervalId);
    };
  }, [buscarUsuarios, verificando]);



  return (
    <>
      <TabelaDados
        data={usuarios}
        extraColumns={extraColumns}
        onModalOpen={handleModalOpen}
      />

      {modalType === 'delete' && (
        <ModalDelete
          visible={modalVisible}
          id={selectedId}
          onCancel={handleModalClose}
          onConfirm={handleDeleteConfirm}
        />
      )}

      {modalType === 'edit' && (
        <ModalEdit
          visible={modalVisible}
          id={selectedId}
          initialData={getSelectedUsuario(selectedId)}
          onCancel={handleModalClose}
          onConfirm={handleEditConfirm}
        />
      )}

      {modalType === 'add' && (
        <ModalAdd
          visible={modalVisible}
          onCancel={handleModalClose}
          onConfirm={handleAddConfirm}
        />
      )}
    </>
  );
};

export default PaginaUsuarios;